import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd, articleJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";
import { relatedByCluster } from "@/lib/clusters";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Göteborg?", a: "Ansökan görs till Social resursförvaltning, tillståndsenheten, i Göteborgs stad. Mölndal, Partille, Härryda och Kungsbacka har egna kommunala enheter." },
  { q: "Vad kostar det i Göteborg?", a: "Göteborgs stad tar ut ansöknings- och tillsynsavgifter som varierar med tillståndstyp och omsättning. Kontrollera aktuella belopp på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden?", a: "Räkna med ca 8–10 veckor för komplett ansökan om stadigvarande tillstånd. Tillfälliga tillstånd kan handläggas snabbare." },
  { q: "Vilka dokument krävs?", a: "Registreringsbevis, finansieringsplan, ritning, hyresavtal, brandskyddsdokumentation och uppgifter om PBI-kretsen." },
];

const URL = "https://alkoholprovet.se/serveringstillstand-goteborg";

export const Route = createFileRoute("/serveringstillstand-goteborg")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Göteborg" },
      ]}
      title="Serveringstillstånd Göteborg – ansökan och krav"
      intro="Komplett guide till serveringstillstånd i Göteborg: var du ansöker, vilka krav som ställs och hur du klarar kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.goteborg, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      localData={{
        unit: "Göteborgs Stad – Social resursförvaltning, tillståndsenheten",
        address: "Köpmansgatan 20, 411 13 Göteborg",
        phone: "031-365 00 00",
        waitTime: "8–10 veckor från komplett ansökan (stadigvarande)",
        feeExamples: [
          "Stadigvarande tillstånd allmänheten: ca 11 000–14 000 kr",
          "Tillfälligt tillstånd allmänheten: ca 6 000–10 000 kr per tillfälle",
          "Tillsynsavgift: fast + omsättningsbaserad rörlig del",
        ],
        checklist: [
          "Registreringsbevis från Bolagsverket",
          "Finansieringsplan + kontoutdrag",
          "Hyresavtal / nyttjanderätt",
          "Planritning över serverings- och uteservering",
          "Brandskyddsdokumentation",
          "Uppgifter om PBI + godkänt kunskapsprov",
        ],
        officialUrl: KOMMUN_LINKS.goteborg,
      }}
      sections={[
        { h2: "Steg-för-steg: ansökan i Göteborg", body: "1. Samla dokumenten enligt checklistan.\n2. Använd Göteborgs stads e-tjänst eller blankett.\n3. Betala ansökningsavgiften.\n4. Boka kunskapsprovet hos tillståndsenheten.\n5. Remisser till polis, miljö och skatteverket.\n6. Beslut – cirka 8–10 veckor från komplett ansökan." },
        { h2: "Krav från Göteborgs stad", body: "Personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal. Från 1 juni 2026 är de nationella kraven på eget kök och varierat matutbud borttagna. Matservering är inte längre ett villkor för tillståndet. Göteborg har lokala riktlinjer för uteserveringar och serveringstider, särskilt i centrum och längs evenemangsstråk." },
        { h2: "Kunskapsprovet i Göteborg", body: "Provet genomförs hos kommunen efter inlämnad ansökan. Frågeantalet varierar beroende på tillståndstyp (FoHMFS 2014:7) men täcker hela alkohollagen. Med Alkoholprovet kan du träna obegränsat hemifrån och göra provsimulering i samma format." },
        { h2: "Lokala förutsättningar", body: "Göteborg är en restaurangtät stad med mycket evenemang. Kommunen prioriterar tillsyn under helger och festivaler. Bra ordnings- och nykterhetsrutiner är centralt för fortsatt tillstånd." },
      ]}
      faqs={faqs}
      related={relatedByCluster("/serveringstillstand-goteborg")}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd i Göteborg – avgifter & handläggning" },
      { name: "description", content: "Allt om hur du söker serveringstillstånd i Göteborg: krav, kostnader och förberedelse inför provet i alkohollagen." },
      { property: "og:title", content: "Serveringstillstånd – Göteborg" },
      { property: "og:description", content: "Allt om serveringstillstånd i Göteborg och hur du klarar kunskapsprovet." },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Göteborg", url: URL },
      ]),
      articleJsonLd({
        url: URL,
        headline: "Serveringstillstånd Göteborg – ansökan och krav",
        description: "Komplett guide till serveringstillstånd i Göteborg: var du ansöker, krav, avgifter, handläggningstid och kunskapsprov.",
        datePublished: "2026-01-15",
        dateModified: LAST_REVIEWED,
      }),
    ],
  }),
});
